import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { z } from "zod";

import { withJWTClient } from "./connectClient";
import type { CatalogAssetMediaType } from "@/types/catalogAssets";

import { type AssetMetadata } from "@/features/AssetUpload/useUploads";
import { getBackendErrorMessage } from "@/utils/backendError";

const API_BASE_URL: string =
  (import.meta.env.VITE_CATALOG_API_URL as string) || "";

const mediaAssetSchema = z
  .object({
    // grpc-gateway serializes int64 as string in JSON; the library endpoint
    // returns a numeric `id`. Accept both and stringify at the call site.
    id: z.union([z.number(), z.string()]),
    handle: z.string().optional(),
    url: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    type: z.string().optional(),
    alt: z.string().nullable().optional(),
    origin: z.string().nullable().optional(),
    width_px: z.number().nullable().optional(),
    height_px: z.number().nullable().optional(),
    file_size: z.number().nullable().optional(),
    upload_status: z.string().optional(),
    approval_status: z.string().optional(),
    source: z.string().optional(),
    mime_type: z.string().nullable().optional(),
    file_name: z.string().nullable().optional(),
  })
  .passthrough();

export type MediaAsset = z.infer<typeof mediaAssetSchema>;

const reserveAssetResponseSchema = z.object({
  asset: mediaAssetSchema,
  upload_url: z.string(),
  method: z.string(),
  expires_at: z.string().optional(),
  content_type: z.string(),
});

export type ReserveAssetResponse = z.infer<typeof reserveAssetResponseSchema>;

export type ReserveAssetParams = {
  file_name: string;
  content_type: string;
  file_size: number;
  type: CatalogAssetMediaType;
  source: string;
  width_px?: number;
  height_px?: number;
  origin?: string;
  handle?: string;
  title?: string;
  alt?: string;
  product_id?: string;
  variant_id?: string;
};

export type CompleteAssetUploadParams = {
  status: "uploaded" | "failed";
  file_size?: number;
  width_px?: number;
  height_px?: number;
  mime_type?: string;
};

export type UploadAssetOptions = {
  signal: AbortSignal;
  onProgress: (percent: number) => void;
};

const ASSET_SOURCE = "user_uploaded";

const mediaTypeForFile = (file: File): CatalogAssetMediaType =>
  file.type.startsWith("video/")
    ? "video"
    : file.type === "text/vcard"
      ? "document"
      : "image";

/**
 * POST /{site_id}/assets/reserve/
 *
 * Creates a pending ProductCatalog asset row and returns a signed GCS `PUT`
 * URL. The frontend uploads the file bytes directly to that URL.
 */
export async function reserveAsset(
  siteId: string,
  params: ReserveAssetParams,
): Promise<ReserveAssetResponse> {
  try {
    const response = await withJWTClient((client) =>
      client.post(`${API_BASE_URL}/${siteId}/assets/reserve/`, params),
    );
    return reserveAssetResponseSchema.parse(response.data);
  } catch (err) {
    throw new Error(`Failed to reserve asset: ${getBackendErrorMessage(err)}`);
  }
}

/**
 * PATCH /{site_id}/assets/{asset_id}/upload/
 *
 * Marks a reserved pending upload as `uploaded` (or `failed`) after the direct
 * GCS upload finishes. ProductCatalog reads the uploaded object metadata and
 * verifies size/content-type against the configured policy and the supplied
 * completion metadata.
 */
export async function completeAssetUpload(
  siteId: string,
  assetId: string,
  params: CompleteAssetUploadParams,
): Promise<MediaAsset> {
  try {
    const response = await withJWTClient((client) =>
      client.patch(
        `${API_BASE_URL}/${siteId}/assets/${assetId}/upload/`,
        params,
      ),
    );
    return mediaAssetSchema.parse(response.data);
  } catch (err) {
    throw new Error(
      `Failed to complete asset upload: ${getBackendErrorMessage(err)}`,
    );
  }
}

/**
 * PUTs file bytes directly to the signed GCS URL returned by ReserveAsset.
 * The URL is signed for a single `PUT` with a fixed `Content-Type`, so the
 * request header must echo the `content_type` from the reservation for the
 * signature to validate.
 */
async function putToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  options: UploadAssetOptions,
): Promise<void> {
  if (options.signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        options.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        options.onProgress(100);
        resolve();
      } else {
        reject(new Error(`GCS PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during GCS PUT"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));

    const onAbort = () => xhr.abort();
    options.signal.addEventListener("abort", onAbort);
    xhr.addEventListener("loadend", () =>
      options.signal.removeEventListener("abort", onAbort),
    );

    xhr.send(file);
  });
}

export async function uploadAsset(
  siteId: string,
  file: File,
  // `metadata` (tags/brand/category/subcategory) is not accepted by the
  // ProductCatalog reserve endpoint yet. The shape is preserved so the
  // manager's signature is stable; wire it into `reserveAsset()` once the
  // backend accepts it.
  _metadata: AssetMetadata,
  options: UploadAssetOptions,
): Promise<MediaAsset> {
  const reservation = await reserveAsset(siteId, {
    file_name: file.name,
    content_type: file.type,
    file_size: file.size,
    type: mediaTypeForFile(file),
    source: ASSET_SOURCE,
  });

  if (options.signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const assetId = String(reservation.asset.id);

  try {
    await putToSignedUrl(
      reservation.upload_url,
      file,
      reservation.content_type,
      options,
    );
  } catch (err) {
    // Best-effort: tell ProductCatalog the reserved row failed so it isn't
    // left dangling. Skip on user-initiated abort (UploadManager removes the
    // item; the pending row is reaped by lifecycle policy) and never let this
    // mask the original upload error.
    if (!options.signal.aborted) {
      await completeAssetUpload(siteId, assetId, { status: "failed" }).catch(
        () => {},
      );
    }
    throw err;
  }

  if (options.signal.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  return completeAssetUpload(siteId, assetId, {
    status: "uploaded",
    file_size: file.size,
    mime_type: file.type,
  });
}

export type UploadAssetVariables = {
  file: File;
  metadata: AssetMetadata;
  options: UploadAssetOptions;
};

/**
 * Shared react-query mutation options for the reserve -> PUT -> complete upload
 * flow. Consumed both by `useUploadAsset` and by `UploadManager` (which runs it
 * imperatively via a `MutationObserver`, since the manager lives outside React).
 */
export const uploadAssetMutationOptions = (siteId: string) =>
  ({
    mutationFn: ({ file, metadata, options }: UploadAssetVariables) =>
      uploadAsset(siteId, file, metadata, options),
  }) satisfies Pick<
    UseMutationOptions<MediaAsset, Error, UploadAssetVariables>,
    "mutationFn"
  >;

export function useUploadAsset(
  siteId: string,
  options?: Omit<
    UseMutationOptions<MediaAsset, Error, UploadAssetVariables>,
    "mutationFn"
  >,
): UseMutationResult<MediaAsset, Error, UploadAssetVariables> {
  return useMutation<MediaAsset, Error, UploadAssetVariables>({
    ...uploadAssetMutationOptions(siteId),
    ...options,
  });
}
